import { useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useHistory } from 'react-router-dom'

import { isSignupComplete, SCREEN_KEY_TO_ROUTE_MAPPING } from '~/constants/ScreenKeyToRouteMapping'
import { Logger } from './logging'
import { secureCache } from './secureCache'
import { storedToken } from './session'

const logger = Logger.getInstance()

interface RouteToNextScreenOptions {
  // Navigate without leaving a history entry, so a back gesture cannot return to the screen
  // that routed. Landing does this; the in-flow screens keep their entries so Back works.
  replace?: boolean
}

/**
 * Routes on the `next_screen` a BFF onboarding endpoint reports. Every onboarding response goes
 * through here so the three outcomes stay consistent across the flow:
 *
 * - onboarding complete: clear the session and log out to /success
 * - a screen key this client has a route for: navigate there
 * - anything else: /error, because a key with no route means the BFF named a screen this client
 *   does not know about, and silently landing somewhere plausible hides that
 */
export const useOnboardingNavigation = () => {
  const history = useHistory()
  const { logout } = useAuth0()

  return useCallback(
    (nextScreen: string | null | undefined, { replace = false }: RouteToNextScreenOptions = {}) => {
      const navigate = (pathname: string) =>
        replace ? history.replace({ pathname }) : history.push({ pathname })

      if (isSignupComplete(nextScreen)) {
        secureCache.clear()
        storedToken.remove()
        logout({ returnTo: window.location.origin + '/success' })
        return
      }

      const route = SCREEN_KEY_TO_ROUTE_MAPPING[nextScreen]
      if (route) {
        navigate(route)
      } else {
        logger.error(`Unknown next_screen: ${nextScreen}`)
        navigate('/error')
      }
    },
    [history, logout],
  )
}
